/**
 * Cross-component, in-memory store of account (company) field edits keyed by
 * account id. Mirrors {@link personEditStore} so edits made in one surface
 * (grid cell, drill-down sheet, meeting briefing, etc.) are visible to every
 * other consumer within the same browser session.
 *
 * Preview/demo-grade state; not persisted. Production sync goes through
 * Apollo cache + backend mutations.
 */

export interface AccountOverride {
  name?: string;
  website?: string;
  websites?: string[];
  phone?: string;
  phones?: string[];
  linkedin?: string;
  location?: string;
  industry?: string;
}

const store = new Map<string, AccountOverride>();
const listeners = new Set<() => void>();

let version = 0;

const notify = () => {
  version += 1;
  for (const l of listeners) l();
};

export const accountEditStore = {
  get(id: string | undefined | null): AccountOverride | undefined {
    if (!id) return undefined;
    return store.get(id);
  },
  set(id: string, override: AccountOverride): void {
    if (!id) return;
    store.set(id, override);
    notify();
  },
  /** Merge a partial override into any existing entry for this id. */
  merge(id: string, partial: AccountOverride): AccountOverride {
    if (!id) return partial;
    const next = { ...(store.get(id) || {}), ...partial };
    store.set(id, next);
    notify();
    return next;
  },
  clear(id: string): void {
    if (!id) return;
    store.delete(id);
    notify();
  },
  getSnapshot(): number {
    return version;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};